import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { CreditCard, Check, Zap, Crown, Building, Tag, Percent, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'free',
    name: 'Free',
    priceMonthly: 0,
    priceAnnual: 0,
    promoPrice: null,
    icon: Zap,
    features: ['1 tournament', '4 teams max', 'Basic features', 'Ads shown']
  },
  {
    id: 'starter',
    name: 'Starter',
    priceMonthly: 15,
    priceAnnual: 162,
    promoPrice: 9.99,
    icon: CreditCard,
    features: ['3 tournaments', '8 teams per tournament', 'No ads', 'Email support', 'Custom colors']
  },
  {
    id: 'pro',
    name: 'Pro',
    priceMonthly: 39,
    priceAnnual: 421.20,
    promoPrice: 29.99,
    icon: Crown,
    popular: true,
    features: ['Unlimited tournaments', '16 teams per tournament', 'Custom branding', 'Priority support', 'Analytics', 'Sponsor management (5 sponsors)']
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    priceMonthly: 99,
    priceAnnual: 1069.20,
    promoPrice: 79.99,
    icon: Building,
    features: ['Everything in Pro', 'White-label solution', 'API access', 'Dedicated support', 'Advanced analytics', 'Unlimited sponsors']
  }
];

export default function BillingPage() {
  const { organization, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Redirect to onboarding if no organization
  useEffect(() => {
    if (!authLoading && !organization) {
      navigate('/onboarding', { replace: true });
    }
  }, [organization, authLoading, navigate]);

  const currentPlan = organization?.plan || 'free';

  // Show loading while checking auth
  if (authLoading || !organization) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await fetch(`${API_URL}/api/billing/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ coupon_code: couponCode })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid coupon');
      }

      setAppliedCoupon(data);
      toast.success(`Coupon applied! ${data.discount_percent}% off`);
    } catch (error) {
      toast.error(String(error.message || 'Invalid coupon'));
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const getPrice = (plan) => {
    const basePrice = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
    if (appliedCoupon && basePrice > 0) {
      return basePrice * (1 - appliedCoupon.discount_percent / 100);
    }
    return basePrice;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingCycle === 'annual' && plan.priceAnnual > 0) {
      const annualPrice = getPrice(plan);
      return (annualPrice / 12).toFixed(2);
    }
    return null;
  };

  const handleUpgrade = async (planId) => {
    setLoading(planId);
    
    try {
      const response = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          billing_cycle: billingCycle,
          coupon_code: appliedCoupon?.code || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout');
      }

      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(String(error.message || 'Failed to create checkout'));
    } finally {
      setLoading(null);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="billing-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight">
            Billing
          </h1>
          <p className="text-gray-400 mt-2">Manage your subscription and billing</p>
        </div>

        {/* Current Plan */}
        <Card className="bg-[#18181b] border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="text-emerald-500" size={24} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Current Plan</p>
                  <p className="text-white text-xl font-bold uppercase">{currentPlan}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Teams limit</p>
                <p className="text-white font-medium">
                  {organization?.limits?.max_teams === -1 ? 'Unlimited' : organization?.limits?.max_teams || 4}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Cycle Toggle */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center bg-[#18181b] border border-white/10 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              data-testid="billing-monthly-toggle"
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-emerald-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              data-testid="billing-annual-toggle"
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-emerald-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Annual
              <span className="px-2 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">
                Save 10%
              </span>
            </button>
          </div>
        </div>

        {/* Coupon Code */}
        <Card className="bg-[#18181b] border-white/10">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <Tag className="text-emerald-500" size={20} />
                <span className="text-white font-medium">Have a coupon code?</span>
              </div>
              {appliedCoupon ? (
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <Percent className="text-emerald-500" size={16} />
                    <span className="text-emerald-500 font-medium">{appliedCoupon.code}</span>
                    <span className="text-emerald-400 text-sm">({appliedCoupon.discount_percent}% off)</span>
                  </div>
                  <Button
                    onClick={removeCoupon}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-1">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    data-testid="coupon-code-input"
                    className="max-w-xs bg-black/20 border-white/10 text-white uppercase"
                    onKeyDown={(e) => e.key === 'Enter' && validateCoupon()}
                  />
                  <Button
                    onClick={validateCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    data-testid="apply-coupon-btn"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {validatingCoupon ? 'Checking...' : 'Apply'}
                  </Button>
                </div>
              )}
            </div>
            {!appliedCoupon && (
              <p className="text-gray-500 text-sm mt-3">
                Try code <span className="text-emerald-500 font-mono">SCORE5</span> for 5% off (valid until April 2026)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const Icon = plan.icon;
            const price = getPrice(plan);
            const monthlyEquiv = getMonthlyEquivalent(plan);
            const hasDiscount = appliedCoupon && price !== (billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly);
            
            return (
              <Card 
                key={plan.id}
                className={`bg-[#18181b] border overflow-hidden ${
                  plan.popular ? 'border-emerald-500 emerald-glow' : 'border-white/10'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="bg-emerald-500 text-white text-xs font-medium text-center py-1">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      plan.popular ? 'bg-emerald-500/20' : 'bg-white/5'
                    }`}>
                      <Icon className={plan.popular ? 'text-emerald-500' : 'text-gray-400'} size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-white uppercase">{plan.name}</h3>
                    </div>
                  </div>

                  <div className="mb-6">
                    {(() => {
                      const hasPromo = plan.promoPrice && billingCycle === 'monthly';
                      if (hasPromo) {
                        return (
                          <>
                            <div className="flex items-baseline gap-2">
                              <span className="text-lg text-gray-500 line-through">${plan.priceMonthly}</span>
                              <span className="text-3xl font-bold text-emerald-400">${plan.promoPrice}</span>
                              <span className="text-gray-500">/month</span>
                            </div>
                            <p className="text-orange-400 text-xs mt-1 font-medium">
                              Limited time — lock in forever
                            </p>
                          </>
                        );
                      }
                      return (
                        <>
                          {hasDiscount && (
                            <span className="text-gray-500 line-through text-lg mr-2">
                              ${billingCycle === 'annual' ? plan.priceAnnual.toFixed(0) : plan.priceMonthly}
                            </span>
                          )}
                          <span className="text-3xl font-bold text-white">${price.toFixed(price % 1 === 0 ? 0 : 2)}</span>
                          <span className="text-gray-500">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                          {monthlyEquiv && (
                            <p className="text-emerald-500 text-sm mt-1">
                              ${monthlyEquiv}/mo equivalent
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check className="text-emerald-500 flex-shrink-0" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan ? (
                    <Button 
                      disabled 
                      className="w-full bg-white/10 text-gray-400"
                    >
                      Current Plan
                    </Button>
                  ) : price === 0 ? (
                    <Button 
                      disabled 
                      className="w-full bg-white/10 text-gray-400"
                    >
                      Free Tier
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={loading === plan.id}
                      className={`w-full ${
                        plan.popular 
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                      data-testid={`upgrade-${plan.id}-btn`}
                    >
                      {loading === plan.id ? 'Processing...' : 'Upgrade'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Annual savings info */}
        {billingCycle === 'annual' && (
          <Card className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border-emerald-500/20">
            <CardContent className="p-4 flex items-center gap-3">
              <Calendar className="text-emerald-500" size={20} />
              <p className="text-gray-300 text-sm">
                <span className="text-emerald-500 font-medium">Annual billing</span> saves you 10% compared to monthly. 
                {appliedCoupon && (
                  <span className="text-yellow-500"> Plus your {appliedCoupon.discount_percent}% coupon discount!</span>
                )}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
